/** Shared media URL + file-classification helpers for the artist portal. */

export type FileGroupId =
  | "mixes"
  | "backing"
  | "vocals"
  | "drums_stems"
  | "other";

export interface FileGroupDef {
  id: FileGroupId;
  label: string;
  order: number;
}

export const FILE_GROUPS: FileGroupDef[] = [
  { id: "mixes", label: "Mixes & Masters", order: 0 },
  { id: "backing", label: "Backing tracks & Instrumentals", order: 1 },
  { id: "vocals", label: "Vocals & Acapellas", order: 2 },
  { id: "drums_stems", label: "Drums & Stems", order: 3 },
  { id: "other", label: "Other files", order: 4 },
];

export function driveIdFromSourcePath(sourcePath?: string): string | null {
  if (!sourcePath) return null;
  const trimmed = sourcePath.trim();
  if (trimmed.startsWith("gdrive:")) return trimmed.slice(7);
  const match = trimmed.match(/\/d\/([^/?#]+)|[?&]id=([^&#]+)/);
  return match?.[1] ?? match?.[2] ?? null;
}

export function getAuthToken(): string | null {
  for (const key of Object.keys(localStorage)) {
    if (key.startsWith("__convexAuthJWT")) {
      return localStorage.getItem(key);
    }
  }
  return null;
}

export function getFileUrl(file: {
  deliveryUrl?: string;
  sourcePath?: string;
  storageProvider?: string;
}): string | undefined {
  const driveId = driveIdFromSourcePath(file.sourcePath);
  const isDriveBacked =
    Boolean(driveId) &&
    (file.storageProvider === "drive" ||
      file.sourcePath?.startsWith("gdrive:") ||
      Boolean(driveId && !file.deliveryUrl));

  if (driveId && isDriveBacked) {
    const token = getAuthToken();
    if (token) {
      return `/api/drive-download?id=${encodeURIComponent(driveId)}&token=${encodeURIComponent(token)}`;
    }
  }

  if (file.deliveryUrl && !/drive\.google\.com/i.test(file.deliveryUrl)) {
    return file.deliveryUrl;
  }

  if (driveId) {
    const token = getAuthToken();
    if (token) {
      return `/api/drive-download?id=${encodeURIComponent(driveId)}&token=${encodeURIComponent(token)}`;
    }
  }

  if (file.deliveryUrl) return file.deliveryUrl;
  if (file.sourcePath && /^https?:\/\//i.test(file.sourcePath)) return file.sourcePath;
  return undefined;
}

export function getDownloadUrl(file: {
  deliveryUrl?: string;
  sourcePath?: string;
  storageProvider?: string;
}): string | undefined {
  const url = getFileUrl(file);
  if (!url) return undefined;
  if (url.startsWith("/api/drive-download?")) {
    return `${url}&purpose=download`;
  }
  return url;
}

export function isAudioFormat(format?: string, title?: string): boolean {
  const text = `${format ?? ""} ${title ?? ""}`.toLowerCase();
  return ["mp3", "wav", "m4a", "aac", "flac", "ogg", "aiff", "aif"].some((ext) =>
    text.includes(ext),
  );
}

export function classifyFileGroup(title: string, type?: string): FileGroupId {
  const v = `${title} ${type ?? ""}`.toLowerCase();

  if (/(vocal|vox|acapella|a cappella|hook|lead vox|topline)/.test(v)) return "vocals";
  if (/(drum|kick|snare|perc|808|hihat|hi-hat|stem|stems|trackout|track out|bus)/.test(v)) {
    return "drums_stems";
  }
  if (/(backing|backtrack|back track|instrumental|inst\b|beat only|no vox)/.test(v)) {
    return "backing";
  }
  if (/(master|final master|full mix|mixdown|warm mix|pro mix|\bmix\b)/.test(v)) {
    return "mixes";
  }
  if (/(instrumental|inst|beat)/.test(v)) return "backing";
  return "other";
}

export function classifyFileIcon(title: string, type?: string): [string, string] {
  const group = classifyFileGroup(title, type);
  const v = `${title} ${type ?? ""}`.toLowerCase();
  if (group === "vocals") return ["vocals", "Vocals"];
  if (group === "drums_stems") {
    if (/(drum|kick|snare|perc|808|hihat)/.test(v)) return ["drums", "Drums"];
    return ["stems", "Stems"];
  }
  if (group === "backing") return ["instrumental", "Instrumental"];
  if (/(master|final master)/.test(v)) return ["master", "Master"];
  if (group === "mixes") return ["mix", "Mix"];
  if (/(guitar|piano|keys|melody|bass)/.test(v)) return ["music", "Music"];
  return ["file", "File"];
}

export function pickDefaultAudioFile<
  T extends {
    id?: string;
    title: string;
    type?: string;
    format?: string;
    access?: string;
    isMainMix?: boolean;
    isFavourite?: boolean;
  },
>(files: T[], mainFileId?: string | null, favouriteFileId?: string | null): T | undefined {
  const audio = files.filter(
    (f) =>
      (f.access === undefined || f.access === "available") &&
      isAudioFormat(f.format, f.title),
  );
  if (audio.length === 0) return undefined;

  // Artist favourite takes priority, then admin main mix pin
  const pinned =
    audio.find((f) => f.isFavourite) ||
    (favouriteFileId ? audio.find((f) => f.id === favouriteFileId) : undefined) ||
    audio.find((f) => f.isMainMix) ||
    (mainFileId ? audio.find((f) => f.id === mainFileId) : undefined);
  if (pinned) return pinned;

  const score = (f: T) => {
    const v = `${f.title} ${f.type ?? ""}`.toLowerCase();
    if (/(final master|\bmaster\b)/.test(v)) return 100;
    if (/(pro mix|warm mix|14lufs|full mix)/.test(v)) return 90;
    if (/\bmix\b/.test(v) && !/(stem|drum|vocal|vox|backing)/.test(v)) return 80;
    if (classifyFileGroup(f.title, f.type) === "mixes") return 70;
    if (classifyFileGroup(f.title, f.type) === "backing") return 40;
    return 10;
  };

  return [...audio].sort((a, b) => score(b) - score(a))[0];
}

export function groupFiles<T extends { title: string; type?: string }>(
  files: T[],
): { group: FileGroupDef; files: T[] }[] {
  const buckets = new Map<FileGroupId, T[]>();
  for (const group of FILE_GROUPS) buckets.set(group.id, []);

  for (const file of files) {
    const id = classifyFileGroup(file.title, file.type);
    buckets.get(id)!.push(file);
  }

  return FILE_GROUPS.map((group) => ({
    group,
    files: buckets.get(group.id) ?? [],
  })).filter((entry) => entry.files.length > 0);
}

export function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}
